import { makeAutoObservable, observable } from 'mobx';
import { sortBy } from 'lodash';
import { i18n } from 'core/localization';
import LocalEventsManager from 'core/localEventsManager';
import { apiFetch } from 'framework/api';
import { ApplicationState } from 'framework/applicationState';
import { ModelValidator } from 'framework/mobx-integration';
import { getMessage } from 'controls/react/ant/antAppWrapper';
import {
	AuthenticationTypeInfo,
	getAuthentication,
	getAuthenticationsByName,
	getAuthenticationTypes,
	OAuthValidationResponse,
	SaveAuthenticationPayload,
	saveAuthentication,
} from 'areas/administration/authentication/api';
import {
	AuthenticationFieldsStore,
	AuthenticationFormConfig,
	AuthenticationFormModel,
	AuthenticationFormMode,
	AuthenticationRecord,
	AuthenticationType,
} from 'areas/administration/authentication/types';
import { OAuth2FieldsStore } from 'areas/administration/authentication/oauth2/components/oauth2-fields/oauth2FieldsStore';
import { SmtpFieldsStore } from 'areas/administration/authentication/smtp/components/smtp-fields/smtpFieldsStore';
import { SimpleFieldsStore } from 'areas/administration/authentication/components/simple-fields/simpleFieldsStore';
import { simpleFieldsConfig } from 'areas/administration/authentication/components/simple-fields/simpleFieldsConfig';
import { OAuthValidationStore } from 'areas/administration/authentication/components/oauth-validation/oauthValidationStore';

export class AuthenticationFormStore {
	model = observable<AuthenticationFormModel>({
		name: '',
		description: '',
		tags: [],
		authenticationType: null,
	});
	validator = new ModelValidator(this.model);

	id: string;
	mode: AuthenticationFormMode;
	accountId: string;
	initialName = '';

	types: AuthenticationTypeInfo[] = [];
	fieldsStore: AuthenticationFieldsStore = null;
	oauthValidation: OAuthValidationStore = null;

	loading = true;
	saving = false;

	constructor(public config: AuthenticationFormConfig) {
		this.id = config.id;
		this.mode = config.mode;
		this.accountId = config.accountId ?? ApplicationState.accountId;

		makeAutoObservable(this, {
			config: false,
		});

		this.validator
			.required('name')
			.required('authenticationType')
			.add('name', {
				message: i18n('Name exists'),
				callbackAsync: () => this.isNameUnique(),
			});
	}

	get readOnly() {
		return this.mode == 'view';
	}

	get isCreate() {
		return this.mode == 'create';
	}

	get valid() {
		return this.validator.valid && (this.fieldsStore?.validator.valid ?? true);
	}

	get okDisabled() {
		return this.loading || this.saving || !this.valid || !!this.oauthValidation?.blocksSave;
	}

	get typeOptions() {
		return this.types.map((x) => ({ label: x.authenticationTypeText, value: x.authenticationType }));
	}

	// returns false when the form can't be shown
	load = async () => {
		try {
			return await this.loadData();
		} catch {
			return false;
		}
	};

	loadData = async () => {
		const [typesResult, recordResult] = await Promise.all([
			apiFetch(getAuthenticationTypes({ types: this.config.authTypes })),
			this.isCreate
				? null
				: apiFetch(
						getAuthentication({ accountId: this.accountId, id: this.id, update: this.mode == 'update' }),
					),
		]);

		if (!typesResult.success || (recordResult && !recordResult.success)) {
			return false;
		}

		this.setLoaded(typesResult.data ?? [], recordResult?.data);
		return true;
	};

	setLoaded(types: AuthenticationTypeInfo[], record: AuthenticationRecord & OAuthValidationResponse) {
		this.types = sortBy(types, (x) => x.authenticationTypeText);

		if (record) {
			this.initialName = record.name ?? '';
			this.model.name = record.name ?? '';
			this.model.description = record.description ?? '';
			this.model.tags = record.tags ?? [];
			this.changeType(record.authenticationType);
			this.fieldsStore?.load(record);
			this.oauthValidation?.applyState(record);
		} else if (this.isCreate && this.types.length == 1) {
			this.changeType(this.types[0].authenticationType);
		}

		this.loading = false;
	}

	changeType(type: AuthenticationType) {
		this.model.authenticationType = type;

		this.fieldsStore?.destroy();
		this.fieldsStore = this.createFieldsStore(type);

		this.oauthValidation = null;
		if (type == AuthenticationType.OAuth2 && this.fieldsStore instanceof SimpleFieldsStore) {
			this.oauthValidation = new OAuthValidationStore(this.fieldsStore);
			if (this.isCreate) {
				this.oauthValidation.showCreateNotice();
			}
		}
	}

	createFieldsStore(type: AuthenticationType): AuthenticationFieldsStore {
		switch (type) {
			case AuthenticationType.OAuth2Rfc6749:
				return new OAuth2FieldsStore(this.readOnly);
			case AuthenticationType.Smtp:
				return new SmtpFieldsStore(this.readOnly);
		}

		const fields = simpleFieldsConfig[type];
		return fields ? new SimpleFieldsStore(fields, this.readOnly) : null;
	}

	isNameUnique = async () => {
		const name = this.model.name.trim();
		if (!name || name.toUpperCase() == this.initialName.trim().toUpperCase()) {
			return true;
		}

		const result = await apiFetch(getAuthenticationsByName({ accountId: this.accountId, name }));
		return !result.success || !result.data?.length;
	};

	get payload(): SaveAuthenticationPayload {
		return {
			id: this.id ?? null,
			accountId: this.accountId,
			name: this.model.name.trim(),
			description: this.model.description?.trim() ?? '',
			tags: this.model.tags ?? [],
			authenticationType: this.model.authenticationType,
			authenticationTypeText: this.types.find((x) => x.authenticationType == this.model.authenticationType)
				?.authenticationTypeText,
			...this.fieldsStore?.payload,
		};
	}

	validate() {
		this.validator.validate();
		const fieldsValid = this.fieldsStore?.validate() ?? true;
		return this.validator.valid && fieldsValid;
	}

	save = async () => {
		if (!this.validate()) {
			return false;
		}

		this.saving = true;
		try {
			const result = await apiFetch(saveAuthentication(this.payload));
			if (!result.success) {
				return false;
			}

			this.onSaved(result.data);

			if (this.isCreate && this.oauthValidation) {
				this.id = result.data.id;
				this.mode = 'update';
				this.initialName = this.model.name;
				this.oauthValidation.start();
				return false;
			}

			return true;
		} finally {
			this.saving = false;
		}
	};

	onSaved(data: AuthenticationRecord) {
		getMessage()?.success?.(
			this.isCreate ? i18n('Authentication successfully created') : i18n('Authentication successfully updated'),
		);
		LocalEventsManager.trigger('authenticationsaved', { success: true, data });
		this.config.onSaved?.(data);
	}

	validateOAuth = async () => {
		if (!this.validate()) {
			return;
		}

		await this.oauthValidation.validate(this.payload);
	};

	destroy() {
		this.validator.destroy();
		this.fieldsStore?.destroy();
	}
}
